import { SISTEMAS } from './evolucionSistema';

const ENCABEZADOS = [...SISTEMAS.map(s => s.label.split(' · ')[0]),
  'Antecedentes', 'Identificación y seguridad', 'Seguridad'];
const normalizar = texto => texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
const escapar = texto => texto.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const ENCABEZADO = new RegExp(`^(${ENCABEZADOS.map(t => escapar(normalizar(t))).join('|')})(?:[.:]\\s*(.*)|\\s*)$`, 'i');
const SISTEMAS_CLINICOS = new Set(SISTEMAS.map(s => normalizar(s.label.split(' · ')[0])));

// El documento se presenta SOLO al mostrarlo o copiarlo. Los renglones del
// borrador y del pulido conservan la identidad de los fragmentos y protegen
// la trayectoria: unirlos antes de las verificaciones cambiaría esas guardas.
// Parte el documento en su preámbulo y sus bloques por encabezado
// ("Neurológico.", "Seguridad."…). Es la MISMA partición que usa la
// presentación; exportada para que el borrador pueda decir qué bloque cambió
// (idea 5 del brainstorm 2026-09-28) sin un segundo parser.
export function partirEnBloques(texto) {
  const src = String(texto || '');
  const preambulo = [];
  const bloques = [];
  let actual = null;
  for (const linea of src.split(/\r?\n/)) {
    const limpia = linea.trim();
    const match = normalizar(limpia).match(ENCABEZADO);
    if (match) {
      // Reconocer sin tildes no autoriza a cambiar la escritura del cuerpo.
      const cuerpo = limpia.slice(match[1].length).replace(/^[.:]\s*/, '');
      actual = { nombre: limpia.slice(0, match[1].length), lineas: cuerpo ? [cuerpo] : [] };
      bloques.push(actual);
    } else if (actual) actual.lineas.push(linea);
    else preambulo.push(linea);
  }
  return { preambulo, bloques };
}

// Nombres (normalizados, sin tildes ni mayúsculas) de los bloques cuyo
// contenido cambió entre dos versiones del documento; un bloque nuevo cuenta
// como cambiado. Puro: sirve para iluminar en el borrador dónde cayó lo nuevo.
export function bloquesQueCambiaron(antes, despues) {
  const mapa = (t) => new Map(partirEnBloques(t).bloques.map((b) => [normalizar(b.nombre), b.lineas.map((l) => l.trim()).filter(Boolean).join('\n')]));
  const a = mapa(antes);
  const d = mapa(despues);
  const cambiados = [];
  for (const [nombre, cuerpo] of d) if (a.get(nombre) !== cuerpo) cambiados.push(nombre);
  return cambiados;
}

export const normalizarNombreBloque = normalizar;

export function formatearEvolucionParaLectura(texto, { integrarExamen = false } = {}) {
  const src = String(texto || '');
  const { preambulo, bloques } = partirEnBloques(src);
  // No reformatear entregas, exámenes ni notas sin estructura de evolución.
  if (!bloques.length) return src;

  const presentados = bloques.map(({ nombre, lineas }) => {
    const clave = normalizar(nombre);
    const contenido = lineas.map(l => l.trim()).filter(Boolean);
    // Corrección ortográfica acotada al nombre de la escala en Seguridad.
    if (clave === 'seguridad') {
      contenido.forEach((l, i) => { contenido[i] = l.replace(/^Dowton(?=\s*[:\d])/i, 'Downton'); });
    }
    // Los límites que ya escribió el autor o eligió el pulido se conservan:
    // no inferimos temas ni convertimos todo el sistema en un solo párrafo.
    // Se retiran los blancos sobrantes, pero cada renglón sigue separado.
    // Invasivos mantiene así una línea por dispositivo. Identificación y
    // Seguridad siguen compactas; sin puntuación, el salto protege el alcance
    // de una negación («Sin dolor» y «Edema en EID» son cláusulas distintas).
    const sistemaClinico = SISTEMAS_CLINICOS.has(clave);
    let cuerpo = contenido.reduce((salida, linea, i) => salida + (i
      ? (sistemaClinico || !/[.!?;:]$/.test(contenido[i - 1]) ? '\n' : ' ')
      : '') + linea, '');
    if (sistemaClinico && clave !== 'invasivos' && !integrarExamen) {
      // El examen abre renglón también si llegó tras otra frase. La puntuación
      // y las palabras se conservan: solo cambia el espacio de separación.
      cuerpo = cuerpo.replace(/([.!?;:,])\s+(?=al examen (?:f[ií]sico|de control)\b)/gi, '$1\n');
    }
    if (clave === 'neurologico') {
      cuerpo = cuerpo.replace(/^Sopor\.(?=\s|$)/i, 'En sopor.');
    }
    return `${nombre}.${cuerpo ? `\n${cuerpo}` : ''}`;
  });
  const inicio = preambulo.join('\n').trim();
  return [...(inicio ? [inicio] : []), ...presentados].join('\n\n');
}
